import { z } from 'zod';
import { AssetTypeSchema, DeviceSlotSchema, ExperimentStateSchema, TargetSchema } from './domain.js';
/**
 * Review and export, as the browser sees it.
 *
 * Three sequential surfaces read one resource: Contact sheet scans every complete
 * Candidate, Play stage puts one of them inside a representative listing, and Packing
 * desk states the exact contents of the archive before it is downloaded. They are
 * stages of one pass, not modes to pick between, so they share one shape.
 */
export const EXPORT_PACKAGES_PATH = '/api/v1/export-packages';
export const TECHNICAL_CHECK_RESOURCE = 'technicalCheck';
export const PLAY_TEST_PAIR_RESOURCE = 'playTestPair';
export const EXPORT_PACKAGE_RESOURCE = 'exportPackage';
/** Play runs the current listing as control against at most this many variants. */
export const PLAY_TEST_PAIR_SLOTS = 2;
/** The longest side of a Contact sheet preview. Previews are derivations, not originals. */
export const PREVIEW_MAX_DIMENSION = 480;
export const RuleSourceSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    url: z.string().min(1),
    verifiedAt: z.string().min(1),
});
/**
 * A hard refusal. Only this can pause an export, and only for the Candidate it names.
 * `authority` separates what Google refuses from what a Studio render preset refuses.
 */
export const TechnicalFailureResourceSchema = z.object({
    code: z.string().min(1),
    artifactId: z.string().nullable(),
    position: z.number().int().nonnegative().nullable(),
    authority: z.enum(['googlePlay', 'studioPreset']),
    expected: z.string(),
    observed: z.string(),
    source: RuleSourceSchema,
});
/** Composition guidance. Shown, dismissed without a record, and never a gate. */
export const CreativeAdviceResourceSchema = z.object({
    code: z.string().min(1),
    artifactId: z.string().nullable(),
    detail: z.string(),
    source: RuleSourceSchema,
});
/** Google's published material is silent or contradicts itself. Say so, do not guess. */
export const VerifyInPlayConsoleResourceSchema = CreativeAdviceResourceSchema;
export const CheckSubjectSchema = z.object({ artifactId: z.string().min(1), contentHash: z.string().min(1) });
export const TechnicalCheckResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    candidateId: z.string().min(1),
    rulesVersion: z.string().min(1),
    verifiedAt: z.string().min(1),
    assetType: AssetTypeSchema,
    subjects: z.array(CheckSubjectSchema),
    failures: z.array(TechnicalFailureResourceSchema),
    advice: z.array(CreativeAdviceResourceSchema),
    verifications: z.array(VerifyInPlayConsoleResourceSchema),
    /** True only when a failure is present. Advice never sets it. */
    blocksExport: z.boolean(),
    /** The bytes or the rules moved since this verdict, so it says nothing about now. */
    stale: z.boolean(),
    checkedAt: z.string().min(1),
});
/**
 * A repair the Studio can perform without anyone deciding anything. Applying it creates
 * an immutable derivative beside the Candidate; the creative source is never replaced.
 */
export const SafeFixResourceSchema = z.object({
    kind: z.string().min(1),
    artifactId: z.string().min(1),
    position: z.number().int().nonnegative().nullable(),
    /** What it does, in one line an operator can read before pressing it. */
    detail: z.string(),
    forFailures: z.array(z.string().min(1)),
    /**
     * Where this repair sits in the sequence. Geometry, then encoding, then the byte
     * ceiling: a recompression performed before a resize is quietly wrong, so the
     * sequence is applied whole and never a step at a time.
     */
    order: z.number().int().nonnegative(),
    /** False where the repair is described but this deployment cannot perform it yet. */
    available: z.boolean(),
});
/** One file of a Candidate as it would be exported: the repair, when one is in use. */
export const ExportFileResourceSchema = z.object({
    position: z.number().int().nonnegative(),
    artifactId: z.string().min(1),
    sourceArtifactId: z.string().min(1),
    representationId: z.string().nullable(),
    mediaType: z.string(),
    byteSize: z.number().int().nonnegative(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    durationMs: z.number().int().nullable(),
    label: z.string(),
});
export const ReviewCandidateSchema = z.object({
    candidateId: z.string().min(1),
    label: z.string(),
    summary: z.string(),
    keep: z.boolean(),
    hidden: z.boolean(),
    assetType: AssetTypeSchema,
    /** Bounded-size derivations, in order. The Contact sheet never loads the originals. */
    previewArtifactIds: z.array(z.string().min(1)),
    files: z.array(ExportFileResourceSchema),
    check: TechnicalCheckResourceSchema.nullable(),
    safeFixes: z.array(SafeFixResourceSchema),
    /** Selected, complete, freshly passed and hash-matched. Nothing else may be packed. */
    exportReady: z.boolean(),
    /** Why it is not ready, in one line. Null when it is. */
    blockedReason: z.string().nullable(),
    slotIndex: z.number().int().positive().nullable(),
});
export const ControlItemSchema = z.object({
    position: z.number().int().nonnegative(),
    artifactId: z.string().nullable(),
    sourceUrl: z.string().nullable(),
    warning: z.string().nullable(),
});
/**
 * What the Candidate is compared against. Before `Mark Live` this is a nullable preview
 * from the latest reviewed Listing Snapshot, and reading or exporting it does not freeze
 * the Experiment's Control.
 */
export const ControlReferenceSchema = z.object({
    source: z.enum(['controlSnapshot', 'listingSnapshotPreview']),
    listingSnapshotId: z.string().nullable(),
    capturedAt: z.string().nullable(),
    items: z.array(ControlItemSchema),
    warnings: z.array(z.string()),
});
export const PlayTestPairSlotSchema = z.object({
    slotIndex: z.number().int().positive(),
    candidateId: z.string().min(1),
    label: z.string(),
});
export const PlayTestPairResourceSchema = z.object({
    experimentId: z.string().min(1),
    version: z.number().int().nonnegative(),
    /** Only for Asset Types whose native eligibility a versioned code capability confirms. */
    eligible: z.boolean(),
    eligibilityNote: z.string(),
    capabilityVerifiedAt: z.string().min(1),
    slots: z.array(PlayTestPairSlotSchema),
});
export const HandoffFactSchema = z.object({
    code: z.string().min(1),
    statement: z.string(),
    limit: z.number().nullable(),
    source: RuleSourceSchema,
});
export const HandoffGuidanceResourceSchema = z.object({
    rulesVersion: z.string().min(1),
    verifiedAt: z.string().min(1),
    blocking: z.literal(false),
    facts: z.array(HandoffFactSchema),
});
export const ExportOmissionSchema = z.object({
    candidateId: z.string().min(1),
    label: z.string(),
    reason: z.string(),
});
export const ExportPackageResourceSchema = z.object({
    id: z.string().min(1),
    version: z.number().int().nonnegative(),
    experimentId: z.string().min(1),
    kind: z.enum(['playTestPair', 'allKept']),
    rootFolder: z.string(),
    fileName: z.string(),
    artifactId: z.string().min(1),
    byteSize: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
    rulesVersion: z.string().min(1),
    omitted: z.array(ExportOmissionSchema),
    warnings: z.array(z.string()),
    createdAt: z.string().min(1),
    /** Downloading is a file transfer. It reaches Play Console in no way at all. */
    downloadPath: z.string().min(1),
});
/** Exactly what the archive will contain, before anything is built. */
export const ExportPreviewSchema = z.object({
    kind: z.enum(['playTestPair', 'allKept']),
    rootFolder: z.string(),
    entries: z.array(z.object({
        folder: z.string(),
        path: z.string(),
        role: z.enum(['control', 'candidate']),
        candidateId: z.string().nullable(),
        candidateLabel: z.string(),
        artifactId: z.string().min(1),
    })),
    includedCandidateIds: z.array(z.string().min(1)),
    omitted: z.array(ExportOmissionSchema),
    control: ControlReferenceSchema.nullable(),
    /** False when nothing selected would survive the admission predicate. */
    ready: z.boolean(),
});
export const ReviewResourceSchema = z.object({
    experimentId: z.string().min(1),
    appId: z.string().min(1),
    appDisplayName: z.string(),
    packageName: z.string().nullable(),
    assetType: AssetTypeSchema,
    target: TargetSchema,
    deviceSlot: DeviceSlotSchema.nullable(),
    state: ExperimentStateSchema,
    rulesVersion: z.string().min(1),
    verifiedAt: z.string().min(1),
    control: ControlReferenceSchema.nullable(),
    candidates: z.array(ReviewCandidateSchema),
    pair: PlayTestPairResourceSchema,
    guidance: HandoffGuidanceResourceSchema,
    packages: z.array(ExportPackageResourceSchema),
});
/**
 * A command that reads or writes bytes was accepted and runs as a Job. What it produced
 * arrives on the stream; nothing about it is waited for inside the request.
 */
export const WorkAcceptedSchema = z.object({ jobId: z.string().min(1) });
export const ExportPackageKindSchema = z.enum(['playTestPair', 'allKept']);
export const CreateExportPackageRequestSchema = z.object({ kind: ExportPackageKindSchema });
export const SetPlayTestPairSlotRequestSchema = z.object({
    slotIndex: z.number().int().min(1).max(PLAY_TEST_PAIR_SLOTS),
    candidateId: z.string().min(1),
});
export function reviewPath(experimentId) {
    return `/api/v1/experiments/${experimentId}/review`;
}
export function playTestPairPath(experimentId) {
    return `/api/v1/experiments/${experimentId}/play-test-pair`;
}
export function exportPreviewPath(experimentId, kind) {
    return `/api/v1/experiments/${experimentId}/export-preview?kind=${kind}`;
}
export function experimentExportPackagesPath(experimentId) {
    return `/api/v1/experiments/${experimentId}/export-packages`;
}
export function exportPackageDownloadPath(packageId) {
    return `${EXPORT_PACKAGES_PATH}/${packageId}/download`;
}
export function technicalCheckPath(candidateId) {
    return `/api/v1/candidates/${candidateId}/technical-check`;
}
export function safeFixPath(candidateId) {
    return `/api/v1/candidates/${candidateId}/safe-fix`;
}
//# sourceMappingURL=export.js.map